import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { documentsAdminV2Service } from '@/features/documents/services/documents-admin-v2.service'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'

type RenameVariables = { id: string; name: string }

/**
 * Las tres cosas que se le pueden hacer a un documento YA subido de un expediente: abrirlo,
 * renombrarlo y quitarlo.
 *
 * ── POR QUÉ UN HOOK APARTE DE `useCreditExpedientMutations` ──────────────────
 *
 * Porque lo consumen DOS pantallas con vidas distintas: el campo del checklist —donde se sube— y
 * la pestaña «Documentos» del panel de detalle, que sólo lista. La segunda no tiene por qué
 * arrastrar las mutaciones de marcar ítems y avanzar etapas para poder ofrecer un botón de
 * descarga.
 *
 * ── EL BORRADO NO REESCRIBE EL CAMPO ────────────────────────────────────────
 *
 * Una sola llamada, no dos. El servidor borra el archivo Y desprende la referencia del campo del
 * checklist en la misma operación; hacerlo desde acá con un `answerField` posterior dejaría una
 * ventana en la que el campo apunta a un archivo que ya no existe — y si el navegador se cierra
 * en el medio, esa ventana no se cierra nunca. Lo único que queda de este lado es refrescar.
 */
export function useCreditDocumentActions(creditId: string) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  const queryClient = useQueryClient()
  const detailKey = queryKeys.credits.detailV2(creditId)

  /**
   * El detalle es la ÚNICA fuente de los documentos del expediente: los adjuntos viajan
   * resueltos dentro de `stages[].items[].fields[].value`. Así que renombrar y borrar se
   * reflejan invalidándolo — no hay una segunda lista que mantener en sincronía.
   */
  const refreshDetail = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: detailKey }),
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.byEntity('credit', creditId) })
    ])
  }

  /**
   * Abrir es pedir la URL firmada y seguirla. **Ver y descargar son lo mismo acá**: el servidor
   * decide por el tipo del archivo si el navegador lo pinta o lo ofrece para guardar.
   *
   * El `<a target="_blank">` sale del molde de adjuntos de negociaciones y clientes
   * (`attachment-manager.tsx`), que es el que la gente ya usa todos los días. No se inventa otro:
   * la firma vence en cinco minutos, así que se pide en el click y no se guarda.
   */
  const download = useMutation({
    mutationFn: async (id: string) => await documentsAdminV2Service.downloadUrl(accessToken, id),
    onSuccess: (result) => {
      const link = document.createElement('a')
      link.href = result.url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  const rename = useMutation({
    mutationFn: async (variables: RenameVariables) =>
      await documentsAdminV2Service.rename(accessToken, variables.id, variables.name),
    onSuccess: async () => {
      toast.success('Nombre actualizado')
      await refreshDetail()
    },
    onError: (error) => {
      // El texto sale del diccionario por CÓDIGO: un expediente aprobado responde
      // `CREDIT_ALREADY_APPROVED` y la pantalla dice «ya fue aprobado y no admite más cambios».
      toast.error(getErrorMessage(error))
    }
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await documentsAdminV2Service.remove(accessToken, id)
    },
    onSuccess: async () => {
      toast.success('Documento eliminado')
      await refreshDetail()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })

  return { download, rename, remove }
}
